import { useCallback, useState } from "react";

const STORAGE_KEY_PREFIX = "midship:evidenceReviewBannerDismissed";

function storageKey(userId: string): string {
  return `${STORAGE_KEY_PREFIX}:${userId}`;
}

function getStoredValue(userId: string | undefined): boolean {
  if (!userId) return false;
  try {
    return localStorage.getItem(storageKey(userId)) === "true";
  } catch {
    return false;
  }
}

/**
 * One-time dismissal state for the "new evidence review" rerun-pending banner.
 *
 * Keyed per user (not per control or workspace): the banner teaches a single
 * transitional fact — regenerate to get the new evidence review — so once a
 * user dismisses it anywhere, it never shows again for them. Stored in
 * localStorage under the user id, so other members of the workspace (and other
 * accounts on a shared machine) are unaffected.
 *
 * The stored flag is read on every render rather than snapshotted into state:
 * `userId` resolves asynchronously with auth, and a mount-time snapshot taken
 * while it was still undefined would resurrect the banner for a user who
 * already dismissed it.
 */
export function useEvidenceReviewBannerDismissed(
  userId: string | undefined,
): [boolean, () => void] {
  const [dismissedThisSession, setDismissedThisSession] = useState(false);

  const dismiss = useCallback(() => {
    if (userId) {
      try {
        localStorage.setItem(storageKey(userId), "true");
      } catch {
        // ignore — worst case the banner reappears next visit
      }
    }
    setDismissedThisSession(true);
  }, [userId]);

  return [dismissedThisSession || getStoredValue(userId), dismiss];
}
